import { useEffect } from 'react';

/**
 * Canonical host. The apex (precisiontrader.tech) 308-redirects to www, so all
 * canonical/OG URLs must use the www host to consolidate ranking signals.
 */
export const SITE_URL = 'https://www.precisiontrader.tech';

// Default share image. Replace with a dedicated 1200x630 asset when available.
const DEFAULT_IMAGE = `${SITE_URL}/logo.png`;

const SEO_ATTR = 'data-managed-seo';

/** og:image and twitter:image must be absolute to be fetchable by crawlers. */
function absoluteUrl(value) {
  if (!value) return value;
  if (/^https?:\/\//i.test(value) || value.startsWith('data:')) return value;
  return `${SITE_URL}${value.startsWith('/') ? '' : '/'}${value}`;
}

function upsertMeta(attr, key, content) {
  if (content == null || typeof document === 'undefined') return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.setAttribute(SEO_ATTR, 'true');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  if (!href || typeof document === 'undefined') return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute(SEO_ATTR, 'true');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function removeManaged(selector) {
  if (typeof document === 'undefined') return;
  const el = document.head.querySelector(selector);
  if (el && el.getAttribute(SEO_ATTR)) el.parentNode.removeChild(el);
}

function upsertJsonLd(id, data) {
  if (typeof document === 'undefined') return;
  if (!data) {
    removeManaged(`script#${id}`);
    return;
  }
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    el.setAttribute(SEO_ATTR, 'true');
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * Per-route SEO metadata. Renders nothing; imperatively manages <head> so that
 * react-snap captures the resulting tags in the prerendered static HTML.
 *
 * Tags are upserted (not removed on unmount) so values carry over and get
 * overwritten as the user navigates between marketing pages.
 */
const Seo = ({
  title,
  description,
  canonicalPath,
  image,
  type = 'website',
  noindex = false,
  schema = null,
  schemaId = 'seo-jsonld',
}) => {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const path = canonicalPath || window.location.pathname || '/';
    const url = `${SITE_URL}${path}`;
    const img = absoluteUrl(image) || DEFAULT_IMAGE;

    if (title) document.title = title;
    upsertMeta('name', 'description', description);
    upsertLink('canonical', url);

    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:image', img);
    upsertMeta('property', 'og:site_name', 'PrecisionTrader');

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', img);

    if (noindex) {
      upsertMeta('name', 'robots', 'noindex, nofollow');
    } else {
      removeManaged('meta[name="robots"]');
    }

    upsertJsonLd(schemaId, schema);
  }, [title, description, canonicalPath, image, type, noindex, schema, schemaId]);

  return null;
};

export default Seo;
